import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Delivery, DeliveryBoardStatus, Supplier } from '@interfaces/ILogistics';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { CalendarDayCell } from './CalendarDayCell';
import { CalendarHeader } from './CalendarHeader';
import { CalendarRail } from './CalendarRail';
import { weekdayLabels } from './calendar.labels';
import { calendarDay, DAYS_PER_WEEK, filterByLegend, gridDays, ordersByDay, overdueOrders, undatedOrders } from './calendar.logic';
import type { ICalendarPeriod } from './useCalendarPeriod';
import type { ILegendFilter } from './useLegendFilter';
import { useMonthCellRows } from './useMonthCellRows';
import styles from './CalendarView.module.css';

const NO_ORDERS: Delivery[] = [];

/** The day an order opened from the side column sits on, flashing until its
 *  animation ends; `count` tells one click from the next. */
interface IDayFlash {
  day: string;
  page: string;
  count: number;
}

export interface ICalendarViewProps {
  deliveries: Delivery[];
  statuses: DeliveryBoardStatus[];
  suppliers: Supplier[];
  tz: string;
  today: string;
  period: ICalendarPeriod;
  filter: ILegendFilter;
  onOpenDelivery: (delivery: Delivery) => void;
}

/** The calendar itself: the header, the month or week grid, the side column. */
export function CalendarView({ deliveries, statuses, suppliers, tz, today, period, filter, onOpenDelivery }: ICalendarViewProps) {
  const { i18n } = useTranslation();
  const { mode, anchor, selected, select, goTo } = period;
  const [flash, setFlash] = useState<IDayFlash | null>(null);
  // A flash belongs to the page it was raised on: paging or switching mode
  // drops it, so coming back never replays it.
  const page = `${mode}:${anchor}`;
  if (flash && flash.page !== page) setFlash(null);
  const endFlash = useCallback(() => setFlash(null), []);
  const days = gridDays(mode, anchor);
  const gridRef = useRef<HTMLDivElement>(null);
  const cellRows = useMonthCellRows(gridRef, days.length / DAYS_PER_WEEK);

  const shown = useMemo(() => filterByLegend(deliveries, filter.picked, statuses, today), [deliveries, filter.picked, statuses, today]);
  const byDay = useMemo(() => ordersByDay(shown, today, tz), [shown, today, tz]);
  // Not memoized: it reads the clock, and the view only renders while it shows.
  const overdue = overdueOrders(shown, new Date(), tz);
  const undated = useMemo(() => undatedOrders(shown, tz), [shown, tz]);

  // From the side column, the grid also goes to the order's day; an undated one has none.
  const openFromRail = (delivery: Delivery) => {
    onOpenDelivery(delivery);
    const day = calendarDay(delivery, tz);
    if (!day) return;
    goTo(day);
    // `goTo` makes the day the anchor; a second click restarts the flash.
    setFlash((current) => ({ day, page: `${mode}:${day}`, count: (current?.count ?? 0) + 1 }));
  };

  return (
    <>
      <CalendarHeader period={period} statuses={statuses} filter={filter} />
      <div className={styles.body}>
        <div className={styles.gridFrame}>
          <div className={styles.weekdays}>
            {weekdayLabels(i18n.language).map((label) => <span key={label}>{label}</span>)}
          </div>
          <ScrollArea className={styles.gridScroll} viewportClassName={styles.gridViewport}>
            <div ref={gridRef} className={styles.grid}>
              {days.map((day) => (
                <CalendarDayCell
                  // A new flash remounts its cell, so a second click replays the animation.
                  key={flash?.day === day ? `${day}:${flash.count}` : day}
                  day={day}
                  orders={byDay.get(day) ?? NO_ORDERS}
                  statuses={statuses}
                  suppliers={suppliers}
                  tz={tz}
                  today={today}
                  mode={mode}
                  cellRows={cellRows}
                  outside={mode === 'month' && day.slice(0, 7) !== anchor.slice(0, 7)}
                  isSelected={day === selected}
                  flash={flash?.day === day}
                  onFlashEnd={endFlash}
                  onSelect={select}
                  onOpenDelivery={onOpenDelivery}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
        <CalendarRail
          day={selected}
          dayOrders={byDay.get(selected) ?? NO_ORDERS}
          overdue={overdue}
          undated={undated}
          statuses={statuses}
          suppliers={suppliers}
          tz={tz}
          today={today}
          onOpenDelivery={openFromRail}
        />
      </div>
    </>
  );
}
